import type { Transfer, TransferInitiatorKind } from "@/types";
import { Badge } from "@/components/ui/badge";
import { IdentifierValue } from "@/components/ui/identifier-value";
import { Progress } from "@/components/ui/progress";
import { formatTransferAmount } from "@/features/transfers/transfer-amount";
import { formatAgo } from "@/lib/format-ago";
import { formatUsd } from "@/lib/format-money";
import { providerLabel } from "@/pages/enterprise/wallets";

/**
 * One row of the transfers list (ALL-5793, ALL-5802). Nine columns, in the header's order:
 * Intent, Status, Amount (with the network under it), From, To, Created, Initiator, Provider,
 * Approval. Every cell is a field the gateway projection carries; there is no direction column
 * because the projection only models outbound sends. Below the md breakpoint the same cells
 * stack into a card, labelled by `data-label`, so phones get one DOM and no horizontal scroll.
 */
export const TRANSFER_LIST_COLUMNS = [
  "Intent",
  "Status",
  "Amount",
  "From",
  "To",
  "Created",
  "Initiator",
  "Provider",
  "Approval",
] as const;

/** ALL-5802: readable label for an initiator's kind. Shared with the detail page's details grid. */
export const INITIATOR_KIND_LABEL: Record<TransferInitiatorKind, string> = {
  human: "human",
  api_key: "API key",
  service: "service",
};

/** A value the gateway did not project: the word "unknown", muted — never blank, never a guess. */
function Unknown() {
  return (
    <span className="text-muted" data-testid="transfer-unknown">
      unknown
    </span>
  );
}

function InitiatorCell({ initiator }: { initiator: Transfer["initiator"] }) {
  const name = initiator?.displayName || initiator?.principalId;
  if (!name) return <Unknown />;
  return (
    <span className="block leading-snug max-md:text-right">
      <span className="block break-words">{name}</span>
      <span className="mt-0.5 block text-xs text-muted">
        {initiator?.kind ? INITIATOR_KIND_LABEL[initiator.kind] : "unknown"}
      </span>
    </span>
  );
}

function ProviderCell({ provider }: { provider: Transfer["provider"] }) {
  const name = provider?.displayName;
  if (!name) return <Unknown />;
  return <span className="block break-words leading-snug max-md:text-right">{providerLabel(name)}</span>;
}

function ApprovalCell({ approval }: { approval: Transfer["approval"] }) {
  const required = approval?.required;
  const approved = approval?.approved;
  if (required == null || approved == null) return <Unknown />;
  const done = required > 0 && approved >= required;
  return (
    <span className="block max-md:text-right" data-testid="transfer-approval">
      <span className="tabular-nums">
        {approved}/{required}
      </span>
      {required > 0 && (
        <Progress
          value={(approved / required) * 100}
          size="sm"
          showLabel={false}
          tone={done ? "success" : "pending"}
          label={`Approval progress ${approved} of ${required}`}
          className="mt-1 w-20 max-md:ml-auto"
        />
      )}
    </span>
  );
}

function exactTime(iso: string | undefined): string | undefined {
  if (!iso) return undefined;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? new Date(ms).toLocaleString() : undefined;
}

function relative(iso: string | undefined, now: number): string {
  const ago = formatAgo(iso, now);
  if (ago) return `${ago} ago`;
  return exactTime(iso) ?? "—";
}

const cell = "px-3 py-3 align-middle max-md:flex max-md:items-center max-md:justify-between max-md:gap-3 max-md:px-0 max-md:py-1 max-md:before:shrink-0 max-md:before:text-xs max-md:before:font-semibold max-md:before:text-muted max-md:before:content-[attr(data-label)]";

export function TransferRow({
  transfer,
  href,
  sourceName,
  now = Date.now(),
}: {
  transfer: Transfer;
  href: string;
  /** Readable source wallet name (from the wallets list); falls back to the projection's own. */
  sourceName?: string;
  now?: number;
}) {
  const from = sourceName || transfer.sourceName || transfer.wallet || "—";
  const updatedDiffers = transfer.updated && transfer.updated !== transfer.created;
  return (
    <tr
      className="border-b border-line last:border-b-0 hover:bg-bg max-md:mb-3 max-md:block max-md:rounded-lg max-md:border max-md:bg-surface max-md:px-4 max-md:py-2 max-md:last:border-b"
      data-testid="transfer-row"
    >
      <td className={cell} data-label="Intent">
        {/* ALL-5792: readable short form, full value on hover/focus, one-click copy. */}
        <IdentifierValue value={transfer.id} kind="intent" label={false} href={href} className="justify-start text-[13px]" />
      </td>
      <td className={cell} data-label="Status">
        {/* Wrapped so the badge keeps its own width when the phone layout stretches the cell. */}
        <span className="inline-flex">
          <Badge variant={transfer.statusVariant} dot={false} size="sm">
            {transfer.statusLabel}
          </Badge>
        </span>
      </td>
      <td className={`${cell} font-mono text-[13px] tabular-nums text-ink`} data-label="Amount">
        <div className="max-md:text-right">
          <span>{formatTransferAmount(transfer.amount)}</span>
          {transfer.asset && <span className="ml-1.5 font-sans text-xs font-semibold text-muted">{transfer.asset}</span>}
          {/* The network sits under the amount (it qualifies the asset), so the table fits a 1280 screen. */}
          <div className="mt-0.5 font-sans text-xs font-normal capitalize text-muted" data-testid="transfer-network">
            {transfer.network || "—"}
          </div>
          {/* A USD line only when the gateway priced the row; an unpriced row is not $0. */}
          {transfer.amountUsd != null && (
            <div data-testid="transfer-amount-usd" className="mt-0.5 font-sans text-xs font-normal text-muted">
              {formatUsd(transfer.amountUsd)}
            </div>
          )}
        </div>
      </td>
      <td className={`${cell} text-[13px] text-text`} data-label="From">
        {/* Wraps instead of truncating, so a derived name keeps its distinguishing short address. */}
        <span className="block max-w-[190px] break-words leading-snug max-md:text-right" title={transfer.wallet ? `${from} (${transfer.wallet})` : from}>{from}</span>
      </td>
      <td className={`${cell} text-[13px]`} data-label="To">
        <IdentifierValue value={transfer.destination} kind="address" label={false} className="justify-start" />
      </td>
      <td className={`${cell} whitespace-nowrap text-[13px] text-muted`} data-label="Created">
        <div>
          <span title={exactTime(transfer.created)}>{relative(transfer.created, now)}</span>
          {updatedDiffers && (
            <div className="mt-0.5 text-xs text-subtle" title={exactTime(transfer.updated)}>
              updated {relative(transfer.updated, now)}
            </div>
          )}
        </div>
      </td>
      {/* ALL-5802: who started it, which provider signed/broadcast it, and how far approval got.
          Each is the projection's own field; a missing one renders "unknown", never a guess. */}
      <td className={`${cell} text-[13px] text-text`} data-label="Initiator">
        <InitiatorCell initiator={transfer.initiator} />
      </td>
      <td className={`${cell} text-[13px] text-text`} data-label="Provider">
        <ProviderCell provider={transfer.provider} />
      </td>
      <td className={`${cell} text-[13px] text-text`} data-label="Approval">
        <ApprovalCell approval={transfer.approval} />
      </td>
    </tr>
  );
}
